import React, { useState, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
} from '../ui'
import { useDepartments, useCreateDepartment, useUpdateDepartment, type Department } from '../../hooks/useDepartments'
import { LoadingSpinner } from '../ui/loading-states'

interface DepartmentFormData {
  name: string
  parent_id?: string | null
  manager_id?: string | null
}

interface DepartmentFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  department?: Department | null
  mode: 'create' | 'edit'
}

export function DepartmentForm({ open, onOpenChange, department, mode }: DepartmentFormProps) {
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    parent_id: null,
    manager_id: null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load parent departments for selection
  const { data: parentDepartments } = useDepartments({
    limit: 100, // Load more for dropdown
  })

  const createDepartment = useCreateDepartment()
  const updateDepartment = useUpdateDepartment()

  const isLoading = createDepartment.isPending || updateDepartment.isPending

  // Initialize form data when dialog opens or department changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && department) {
        setFormData({
          name: department.name || '',
          parent_id: department.parent_id || null,
          manager_id: department.manager_id || null,
        })
      } else {
        setFormData({
          name: '',
          parent_id: null,
          manager_id: null,
        })
      }
      setErrors({})
    }
  }, [open, mode, department])

  const handleInputChange = (field: keyof DepartmentFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value: string | null = e.target.value

    // Convert empty string to null for optional UUID fields
    if (value === '' && (field === 'parent_id' || field === 'manager_id')) {
      value = null
    }


    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название подразделения обязательно'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Название должно содержать минимум 2 символа'
    }

    // Don't allow parent to be self (for edit mode)
    if (mode === 'edit' && department && formData.parent_id === department.id) {
      newErrors.parent_id = 'Подразделение не может быть родительским для самого себя'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Prepare data for sending - ensure proper null/undefined handling
      const dataToSend: any = {
        name: formData.name.trim(),
      }

      // Handle optional UUID fields - send null for empty values, or valid UUID
      if (formData.parent_id && formData.parent_id !== null && formData.parent_id.trim() !== '') {
        dataToSend.parent_id = formData.parent_id.trim()
      } else {
        dataToSend.parent_id = null
      }

      if (formData.manager_id && formData.manager_id !== null && formData.manager_id.trim() !== '') {
        dataToSend.manager_id = formData.manager_id.trim()
      } else {
        dataToSend.manager_id = null
      }


      if (mode === 'create') {
        await createDepartment.mutateAsync(dataToSend)
      } else if (mode === 'edit' && department) {
        await updateDepartment.mutateAsync({
          id: department.id,
          data: dataToSend
        })
      }
      onOpenChange(false)
    } catch (error) {
      // Error handling will be done by React Query and shown in UI
      console.error('Form submission error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать подразделение' : 'Редактировать подразделение'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Заполните информацию для создания нового подразделения.'
              : 'Измените информацию о подразделении.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Department Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Название подразделения *
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange('name')}
              placeholder="Введите название подразделения"
              disabled={isLoading}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.name}
              </div>
            )}
          </div>

          {/* Parent Department */}
          <div>
            <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700 mb-1">
              Родительское подразделение
            </label>
            <Select
              id="parent_id"
              value={formData.parent_id || ''}
              onChange={handleInputChange('parent_id')}
              disabled={isLoading}
              className={errors.parent_id ? 'border-red-500' : ''}
            >
              <SelectOption value="">Нет (корневое подразделение)</SelectOption>
              {parentDepartments?.data
                ?.filter(dept => mode === 'create' || dept.id !== department?.id) // Don't show self in edit mode
                ?.map(dept => (
                  <SelectOption key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectOption>
                ))
              }
            </Select>
            {errors.parent_id && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.parent_id}
              </div>
            )}
          </div>

          {/* Manager (placeholder - would need employees data) */}
          <div>
            <label htmlFor="manager_id" className="block text-sm font-medium text-gray-700 mb-1">
              Руководитель
            </label>
            <Select
              id="manager_id"
              value={formData.manager_id || ''}
              onChange={handleInputChange('manager_id')}
              disabled={isLoading}
            >
              <SelectOption value="">Не назначен</SelectOption>
              {/* TODO: Load employees for manager selection */}
            </Select>
          </div>

          {/* Show API errors */}
          {(createDepartment.error || updateDepartment.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(createDepartment.error as any)?.message ||
                 (updateDepartment.error as any)?.message ||
                 'Произошла ошибка при сохранении'}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}